import filters from './filters';
import layout from './layout';
import ui from './ui';
import testing from './testing';
import eventTypes from './analytics';
import calculator from './calculator';
import FEATURE_GROUPS from './featureGroups';
import sorting from './sorting';
import isSSR from '@/utils/isSSR';
const { options: SORTING_OPTIONS } = sorting;

type Env = 'development' | 'staging' | 'production' | 'local';

enum ProcessEnvKey {
  LOG_LEVEL = 'LOG_LEVEL',
  NODE_ENV = 'NODE_ENV',
  ENV_NAME = 'ENV_NAME',
  FRONT_URL = 'FRONT_URL',
  MEMORY_TOTAL_MB = 'MEMORY_TOTAL_MB',
  BASIC_AUTH_USER = 'BASIC_AUTH_USER',
  BASIC_AUTH_PASSWORD = 'BASIC_AUTH_PASSWORD',
  CMS_URL = 'CMS_URL',
  CMS_API_KEY = 'CMS_API_KEY',
  CMS_X_API_KEY = 'CMS_X_API_KEY',
  CMS_ANCHESTOR_ID = 'CMS_ANCHESTOR_ID',
  OPMID_URL = 'OPMID_URL',
  DEMOGRAPHY_API_URL = 'DEMOGRAPHY_API_URL',
  DEMOGRAPHY_API_ROLE = 'DEMOGRAPHY_API_ROLE',
  DEMOGRAPHY_API_KEY = 'DEMOGRAPHY_API_KEY',
  DEMOGRAPHY_API_SUBSCRIPTION_KEY = 'DEMOGRAPHY_API_SUBSCRIPTION_KEY',
  BACKEND_URL = 'BACKEND_URL',
  KOTIPRO_BACKEND_URL = 'KOTIPRO_BACKEND_URL',
  USE_KOTIPRO_BACKEND = 'USE_KOTIPRO_BACKEND',
  STATIC_ENDPOINT = 'STATIC_ENDPOINT',
  GOOGLE_CLIENT_KEY = 'GOOGLE_CLIENT_KEY',
  RECAPTCHA_TOKEN = 'RECAPTCHA_TOKEN',
  RUUVALLI_API_URL = 'RUUVALLI_API_URL',
  RUUVALLI_API_KEY = 'RUUVALLI_API_KEY',
  RUUVALLI_CLIENT_ROLE = 'RUUVALLI_CLIENT_ROLE',
  REDIS_URL = 'REDIS_URL',
  REDIS_ENABLED = 'REDIS_ENABLED',
  GIT_TAG = 'GIT_TAG',
  GIT_REV = 'GIT_REV',
  LRU_CACHE_TTL = 'LRU_CACHE_TTL',
}

function getVersion() {
  const tag = getSsrEnv(ProcessEnvKey.GIT_TAG, undefined);
  const rev = getSsrEnv(ProcessEnvKey.GIT_REV, undefined);
  if (tag) {
    return tag + ' (' + rev + ')';
  }
  return rev;
}

export const getClientLogLevel = () => {
  if (isSSR && getSsrEnv(ProcessEnvKey.ENV_NAME, '') === 'production') {
    return 'ERROR';
  } else if (isSSR) {
    return process.env[ProcessEnvKey.LOG_LEVEL] || 'ERROR';
  }
};

const getSsrEnv = (envKey: ProcessEnvKey, defaultVal: string = ''): string => {
  if (isSSR) {
    return process.env[envKey] || defaultVal;
  } else {
    if (envKey === ProcessEnvKey.LOG_LEVEL) {
      return window.LOG_LEVEL || defaultVal;
    }
  }
  return defaultVal;
};

export default {
  LOG_LEVEL: getSsrEnv(ProcessEnvKey.LOG_LEVEL, 'ERROR'),
  MEMORY_TOTAL_MB: getSsrEnv(ProcessEnvKey.MEMORY_TOTAL_MB, '2048') as Env, // Server max memory to be used in health check calculation
  env: getSsrEnv(ProcessEnvKey.ENV_NAME, '') as Env,
  useMock: getSsrEnv(ProcessEnvKey.ENV_NAME, '') === 'local',
  isProdBuild: getSsrEnv(ProcessEnvKey.NODE_ENV) === 'production',
  version: getVersion(),
  server: {
    port: 4000,
  },
  analytics: {
    scriptSrc: {
      development: 'https://assets.adobedtm.com/launch-EN12b72e9ff3cc4ddf81af6bcf8dcef744-development.min.js',
      staging: 'https://assets.adobedtm.com/launch-EN07c811b1c7bb4016904f4375401c574c-staging.min.js',
      production: 'https://assets.adobedtm.com/launch-ENa43f218fc7904e81b64928a93559d822.min.js',
      local: 'https://assets.adobedtm.com/launch-EN12b72e9ff3cc4ddf81af6bcf8dcef744-development.min.js',
    },
    eventTypes: eventTypes,
  },
  FRONT: {
    URL: getSsrEnv(ProcessEnvKey.FRONT_URL, 'http://localhost:4000'),
    PROD_BASE_URL: 'https://op-koti.fi',
  },
  BASIC_AUTH: {
    USER: getSsrEnv(ProcessEnvKey.BASIC_AUTH_USER),
    PASSWORD: getSsrEnv(ProcessEnvKey.BASIC_AUTH_PASSWORD),
  },
  DEMOGRAPHY: {
    URL: getSsrEnv(ProcessEnvKey.DEMOGRAPHY_API_URL, undefined),
    CLIENT_ROLE: getSsrEnv(ProcessEnvKey.DEMOGRAPHY_API_ROLE, undefined),
    API_KEY: getSsrEnv(ProcessEnvKey.DEMOGRAPHY_API_KEY, undefined),
    SUBSCRIPTION_KEY: getSsrEnv(ProcessEnvKey.DEMOGRAPHY_API_SUBSCRIPTION_KEY, undefined),
  },
  BACKEND: {
    URL: getSsrEnv(ProcessEnvKey.BACKEND_URL, 'http://localhost:9000/api/v1'),
    STATIC_ENDPOINT: getSsrEnv(ProcessEnvKey.STATIC_ENDPOINT, 'http://localhost:9000/api/v1/static'),
    USE_KOTIPRO: getSsrEnv(ProcessEnvKey.USE_KOTIPRO_BACKEND, 'false') === 'true',
    URL_KOTIPRO: getSsrEnv(ProcessEnvKey.KOTIPRO_BACKEND_URL, 'http://localhost:3000/api'),
  },
  CMS: {
    URL: getSsrEnv(ProcessEnvKey.CMS_URL),
    /** API Key used to access Optimizely without Apigee. */
    API_KEY: getSsrEnv(ProcessEnvKey.CMS_API_KEY),
    /** API Key used to access Optimizely via Apigee. */
    X_API_KEY: getSsrEnv(ProcessEnvKey.CMS_X_API_KEY),
    /** The Guid of the CMS folder that contains the data */
    ANCHESTOR_ID: getSsrEnv(ProcessEnvKey.CMS_ANCHESTOR_ID),
  },
  OPMID: {
    URL: getSsrEnv(ProcessEnvKey.OPMID_URL),
  },
  LRU_CACHE: {
    TTL: getSsrEnv(ProcessEnvKey.LRU_CACHE_TTL, '900000'),
  },
  LISTING_SOURCES: {
    NEWSEC: 'Newsec',
    OP_KIINTEISTOKESKUS: 'Kipinä',
    GENERATED: 'Generated',
  },
  GOOGLE: {
    BASE_URL: 'https://maps.googleapis.com/maps/api/js',
    API_KEY: getSsrEnv(ProcessEnvKey.GOOGLE_CLIENT_KEY, undefined),
    CLIENT_ID: 'gme-opfinancialgroup',
  },
  RECAPTCHA: {
    TOKEN: getSsrEnv(ProcessEnvKey.RECAPTCHA_TOKEN),
  },
  RUUVALLI: {
    URL: getSsrEnv(ProcessEnvKey.RUUVALLI_API_URL, 'localhost'),
    API_KEY: getSsrEnv(ProcessEnvKey.RUUVALLI_API_KEY, ''),
    CLIENT_ROLE: getSsrEnv(ProcessEnvKey.RUUVALLI_CLIENT_ROLE, 'opkoti'),
  },
  REDIS: {
    URL: getSsrEnv(ProcessEnvKey.REDIS_URL, 'redis://localhost:6379'),
    ENABLED: getSsrEnv(ProcessEnvKey.REDIS_ENABLED) === 'true',
  },
  //  LAYOUT CONFIGURATION
  // Must match breakpoints.pcss
  BREAKPOINTS: layout.BREAKPOINTS,
  MOBILE_TABS: layout.MOBILE_TABS,
  DESKTOP_MEDIA_QUERY: layout.DESKTOP_MEDIA_QUERY,

  // UI CONFIGURATION
  COMMISSION_TYPES: ui.COMMISSION_TYPES,
  LANDING_PAGES: ui.LANDING_PAGES,
  PAGE_NAMES: ui.PAGE_NAMES,
  LANGUAGES: ui.LANGUAGES,
  LISTING_TYPES: ui.LISTING_TYPES,
  APARTMENT_CONDITIONS: ui.APARTMENT_CONDITIONS,
  TOP_AGENCIES: ui.TOP_AGENCIES,
  TOP_SERVICES: ui.TOP_SERVICES,
  weekdays: ui.weekdays,
  ARTICLE_TAGS: ui.ARTICLE_TAGS,
  ACCEPTED_INPUT_TYPES: ui.ACCEPTED_INPUT_TYPES,
  SCROLL_SECTIONS: ui.SCROLL_SECTIONS,
  TEXT_INPUT_DEBOUNCE: ui.TEXT_INPUT_DEBOUNCE,
  CONDITION_TYPES: ui.CONDITION_TYPES,
  TYPES: ui.TYPES,

  //  FILTER CONFIGURATION
  SEARCH_MODES: filters.SEARCH_MODES,
  FILTER_MAPPING: filters.FILTER_MAPPING,
  FILTER_KEYS: filters.FILTER_KEYS,
  SHOWINGS: filters.SHOWINGS,
  FILTER_LIMITS: filters.FILTER_LIMITS,
  EXTRA_FEATURES: filters.EXTRA_FEATURES,
  SITE_OWNERSHIP: filters.SITE_OWNERSHIP,
  FILTER_DEFAULTS: filters.FILTER_DEFAULTS,
  // Only location filters that need to wait for name lookup (don't put e.g. postalCode here)
  LOCATION_FILTERS: filters.LOCATION_FILTERS,
  FEATURE_GROUPS: FEATURE_GROUPS,
  SORTING_OPTIONS: SORTING_OPTIONS,
  testing: testing,
  GDPR_CONSENT_OPTIONS: {
    ALLOW_ALL: 1,
    DISALLOW_TARGETED_ADS: 2,
    DISALLOW_ALL: 3,
  },

  // CALCULATOR CONFIGURATION
  BUILDING_TYPES: calculator.BUILDING_TYPES,
  BUILDING_TYPE: calculator.BUILDING_TYPE,
  CONDITIONS: calculator.CONDITIONS,
  LIMITS: calculator.LIMITS,
  CONFIDENCE_CLASS_LIMIT: calculator.CONFIDENCE_CLASS_LIMIT,

  BUILDING_TYPE_NAMES: ui.BUILDING_TYPE_NAMES,
};
